/**
 * Progress Components
 *
 * A collection of progress indicators for various use cases:
 * - ProgressBar: Linear progress bar
 * - ProgressCircle: Circular progress indicator
 * - ProgressSteps: Multi-step progress indicator
 * - ProgressRing: Small ring indicator
 *
 * Usage:
 * <ProgressBar value={75} />
 * <ProgressCircle value={60} size={80} />
 * <ProgressSteps steps={['Start', 'Process', 'Complete']} current={1} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Check, Circle, Loader2 } from 'lucide-react';

/**
 * Color presets
 */
const colorPresets = {
  purple: {
    bar: 'bg-primary-500',
    track: 'bg-primary-500/20',
    text: 'text-primary-400',
    ring: 'stroke-purple-500',
  },
  blue: {
    bar: 'bg-secondary-500',
    track: 'bg-secondary-500/20',
    text: 'text-secondary-400',
    ring: 'stroke-blue-500',
  },
  green: {
    bar: 'bg-success-500',
    track: 'bg-success-500/20',
    text: 'text-success-400',
    ring: 'stroke-green-500',
  },
  amber: {
    bar: 'bg-warning-500',
    track: 'bg-warning-500/20',
    text: 'text-warning-400',
    ring: 'stroke-amber-500',
  },
  red: {
    bar: 'bg-error-500',
    track: 'bg-error-500/20',
    text: 'text-error-400',
    ring: 'stroke-red-500',
  },
  gradient: {
    bar: 'bg-gradient-to-r from-primary-500 via-secondary-500 to-success-500',
    track: 'bg-white/10',
    text: 'text-white',
    ring: 'stroke-purple-500',
  },
};

/**
 * ProgressBar - Linear progress bar
 */
export const ProgressBar = ({
  value = 0,
  max = 100,
  color = 'purple',
  size = 'md',
  showLabel = false,
  showValue = false,
  label = '',
  animated = true,
  striped = false,
  indeterminate = false,
  className = '',
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const colors = colorPresets[color] || colorPresets.purple;

  const sizeClasses = {
    xs: 'h-1',
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
    xl: 'h-4',
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Label and value */}
      {(showLabel || showValue) && (
        <div className="flex justify-between items-center mb-1.5">
          {showLabel && (
            <span className="text-sm text-gray-400">{label}</span>
          )}
          {showValue && (
            <span className={`text-sm font-medium ${colors.text}`}>
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div
        className={`w-full ${sizeClasses[size]} ${colors.track} rounded-full overflow-hidden`}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label || 'Progress'}
      >
        <div
          className={`
            h-full ${colors.bar} rounded-full
            ${animated ? 'transition-all duration-500 ease-out' : ''}
            ${striped ? 'bg-stripes' : ''}
            ${indeterminate ? 'animate-indeterminate w-1/3' : ''}
          `}
          style={indeterminate ? {} : { width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  value: PropTypes.number,
  max: PropTypes.number,
  color: PropTypes.oneOf(['purple', 'blue', 'green', 'amber', 'red', 'gradient']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  showLabel: PropTypes.bool,
  showValue: PropTypes.bool,
  label: PropTypes.string,
  animated: PropTypes.bool,
  striped: PropTypes.bool,
  indeterminate: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * ProgressCircle - Circular progress indicator
 */
export const ProgressCircle = ({
  value = 0,
  max = 100,
  size = 64,
  strokeWidth = 4,
  color = 'purple',
  showValue = true,
  label = '',
  animated = true,
  className = '',
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const colors = colorPresets[color] || colorPresets.purple;

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label || 'Progress'}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-white/10"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className={colors.ring}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: strokeDashoffset,
            transition: animated ? 'stroke-dashoffset 0.5s ease-out' : 'none',
          }}
        />
      </svg>

      {/* Center content */}
      {showValue && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-sm font-bold ${colors.text}`}>
            {Math.round(percentage)}%
          </span>
          {label && (
            <span className="text-[10px] text-gray-500">{label}</span>
          )}
        </div>
      )}
    </div>
  );
};

ProgressCircle.propTypes = {
  value: PropTypes.number,
  max: PropTypes.number,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  color: PropTypes.oneOf(['purple', 'blue', 'green', 'amber', 'red', 'gradient']),
  showValue: PropTypes.bool,
  label: PropTypes.string,
  animated: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * ProgressSteps - Multi-step progress indicator
 */
export const ProgressSteps = ({
  steps = [],
  current = 0,
  color = 'purple',
  size = 'md',
  orientation = 'horizontal',
  showLabels = true,
  className = '',
}) => {
  const colors = colorPresets[color] || colorPresets.purple;

  const sizeClasses = {
    sm: { step: 'w-6 h-6 text-xs', connector: 'h-0.5', label: 'text-xs' },
    md: { step: 'w-8 h-8 text-sm', connector: 'h-1', label: 'text-sm' },
    lg: { step: 'w-10 h-10 text-base', connector: 'h-1', label: 'text-base' },
  };

  const sizes = sizeClasses[size];
  const isVertical = orientation === 'vertical';

  return (
    <div
      className={`
        flex ${isVertical ? 'flex-col' : 'items-center'}
        ${className}
      `}
      role="navigation"
      aria-label="Progress steps"
    >
      {steps.map((step, index) => {
        const isCompleted = index < current;
        const isCurrent = index === current;
        const isLast = index === steps.length - 1;

        return (
          <React.Fragment key={index}>
            {/* Step */}
            <div
              className={`
                flex ${isVertical ? 'items-start' : 'flex-col items-center'}
                ${isVertical ? '' : 'flex-1'}
              `}
            >
              {/* Step indicator */}
              <div
                className={`
                  flex items-center justify-center rounded-full
                  transition-all duration-300
                  ${sizes.step}
                  ${isCompleted
                    ? `${colors.bar} text-white`
                    : isCurrent
                      ? `${colors.track} ${colors.text} ring-2 ring-offset-2 ring-offset-gray-900 ${colors.ring.replace('stroke', 'ring')}`
                      : 'bg-white/10 text-gray-500'
                  }
                `}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {isCompleted ? (
                  <Check className="w-4 h-4" />
                ) : isCurrent ? (
                  <Circle className="w-3 h-3 fill-current" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>

              {/* Label */}
              {showLabels && (
                <span
                  className={`
                    mt-2 ${sizes.label}
                    ${isCurrent ? 'text-white font-medium' : 'text-gray-500'}
                    ${isVertical ? 'ml-3 mt-0' : 'text-center'}
                  `}
                >
                  {typeof step === 'string' ? step : step.label}
                </span>
              )}
            </div>

            {/* Connector */}
            {!isLast && (
              <div
                className={`
                  ${isVertical
                    ? `w-0.5 h-8 ml-4 my-1`
                    : `flex-1 ${sizes.connector} mx-2`
                  }
                  ${isCompleted ? colors.bar : 'bg-white/10'}
                  transition-colors duration-300
                `}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

ProgressSteps.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
      }),
    ])
  ).isRequired,
  current: PropTypes.number,
  color: PropTypes.oneOf(['purple', 'blue', 'green', 'amber', 'red']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  showLabels: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * ProgressRing - Small inline ring indicator
 */
export const ProgressRing = ({
  value = 0,
  size = 16,
  strokeWidth = 2,
  color = 'purple',
  className = '',
}) => {
  const percentage = Math.min(100, Math.max(0, value));
  const colors = colorPresets[color] || colorPresets.purple;

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      className={`transform -rotate-90 ${className}`}
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-white/10"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        className={colors.ring}
        style={{
          strokeDasharray: circumference,
          strokeDashoffset: strokeDashoffset,
          transition: 'stroke-dashoffset 0.3s ease-out',
        }}
      />
    </svg>
  );
};

ProgressRing.propTypes = {
  value: PropTypes.number,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  color: PropTypes.oneOf(['purple', 'blue', 'green', 'amber', 'red']),
  className: PropTypes.string,
};

/**
 * LoadingProgress - Indeterminate loading indicator with optional message
 */
export const LoadingProgress = ({
  message = 'Loading...',
  size = 'md',
  showSpinner = true,
  className = '',
}) => {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const spinnerSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {showSpinner && (
        <Loader2 className={`${spinnerSizes[size]} text-primary-400 animate-spin`} />
      )}
      <span className={`text-gray-400 ${sizeClasses[size]}`}>{message}</span>
    </div>
  );
};

LoadingProgress.propTypes = {
  message: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showSpinner: PropTypes.bool,
  className: PropTypes.string,
};

// Default export
export default ProgressBar;
